import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { PublicShell, AuthShell } from './shell/PublicShell';
import { AppShell } from './shell/AppShell';

/** Preserve the :id when redirecting /tpo/companies/:id → /tpo/partners/:id. */
function TpoCompaniesRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/tpo/partners/${id ?? ''}`} replace />;
}

// Public
import { LandingPage } from './pages/public/Landing';
import { NotFoundPage } from './pages/public/NotFound';
import { LoginPage } from './pages/public/Login';
import { RegisterPage } from './pages/public/Register';
import { RegisterStudentPage } from './pages/public/RegisterStudent';
import { RegisterInstitutionPage } from './pages/public/RegisterInstitution';
import { RegisterPartnerPage } from './pages/public/RegisterPartner';
import { VerifyEmailPage } from './pages/public/VerifyEmail';
import { ForgotPasswordPage } from './pages/public/ForgotPassword';
import { OnboardingPage } from './pages/public/Onboarding';
import { PricingPage } from './pages/public/Pricing';
import { ForCompaniesPage } from './pages/public/ForCompanies';
import { ForStudentsPage } from './pages/public/ForStudents';
import { ForInstitutionsPage } from './pages/public/ForInstitutions';
import { ForPartnersPage } from './pages/public/ForPartners';
import { SecurityPage } from './pages/public/Security';
import { ContactPage } from './pages/public/Contact';
import { ErrorPage } from './pages/public/Error';
import { AcceptInvitePage } from './pages/public/AcceptInvite';
import { SignInLinkPage } from './pages/public/SignInLink';
import { CheckoutPage } from './pages/public/Checkout';
import { AboutPage } from './pages/public/About';
import { TermsPage } from './pages/public/Terms';
import { PrivacyPublicPage } from './pages/public/PrivacyPublic';
import { StatusPage } from './pages/public/Status';
import { CustomersPage } from './pages/public/Customers';
import { ChangelogPage } from './pages/public/Changelog';
import { AiPage } from './pages/public/Ai';
import { AssessmentsPage } from './pages/public/Assessments';

// Role-home gate
import { RoleHomeGate } from './shell/RoleHomeGate';

// Student
import { StudentHomePage } from './pages/student/Home';
import { ExplorePage } from './pages/student/Explore';
import { StudentEligibleDrivesPage } from './pages/student/EligibleDrives';
import { StudentOpportunityDetailPage } from './pages/student/OpportunityDetail';
import { TenantOpportunitiesPage } from './pages/shared/TenantOpportunities';
import { StudentApplicationsPage } from './pages/student/Applications';
import { StudentApplicationDetailPage } from './pages/student/ApplicationDetail';
import { OfferDetailPage } from './pages/student/OfferDetail';
import { StudentOffersPage } from './pages/student/Offers';
import { InterviewDetailPage } from './pages/student/InterviewDetail';
import { StudentInterviewsPage } from './pages/student/Interviews';
import { StudentProfilePage } from './pages/student/Profile';
import { StudentProfile360Page } from './pages/student/Profile360';
import { StudentIntegrityPage } from './pages/student/Integrity';
import { StudentActivityPage } from './pages/student/Activity';
import { StudentMembershipsPage } from './pages/student/Memberships';
import { BadgeDetailPage } from './pages/student/BadgeDetail';
import { StudentResumeBuilderPage } from './pages/student/ResumeBuilder';
import { StudentMockPage } from './pages/student/Mock';
import { StudentMockSessionPage } from './pages/student/MockSession';
import { StudentAdvisorPage } from './pages/student/Advisor';
import { StudentUploadMarksPage } from './pages/student/UploadMarks';
import { StudentPrepPage } from './pages/student/Prep';
import { StudentAcademicsPage } from './pages/student/Academics';
import { StudentDocumentsPage } from './pages/student/Documents';
import { StudentSkillsPage } from './pages/student/Skills';
import { StudentProjectsPage } from './pages/student/Projects';
import { StudentStatsPage } from './pages/student/Stats';
import { StudentTestsPage } from './pages/student/Tests';
import { StudentSavedPage } from './pages/student/Saved';
import { StudentInternshipsPage } from './pages/student/Internships';
import { StudentProgramsPage } from './pages/student/Programs';

// TPO
import { TpoTodayPage } from './pages/tpo/Today';
import { TpoDrivesPage } from './pages/tpo/Drives';
import { PostDrivePage } from './pages/tpo/PostDrive';
import { DriveDetailPage } from './pages/tpo/DriveDetail';
import { TpoApplicationDetailPage } from './pages/tpo/ApplicationDetail';
import { TpoCalendarPage } from './pages/tpo/Calendar';
import { TpoOffersPage } from './pages/tpo/Offers';
import { TpoStudentsPage } from './pages/tpo/Students';
import { TpoClaimQueuePage } from './pages/tpo/ClaimQueue';
import { TpoAutoApprovedMembershipsPage } from './pages/tpo/AutoApproved';
import { TpoDomainsPage } from './pages/tpo/Domains';
import { TpoCampusInvitationsPage } from './pages/tpo/CampusInvitations';
import { TpoGraduationBatchPage } from './pages/tpo/GraduationBatch';
import { TpoPartnersPage } from './pages/tpo/Partners';
import { TpoPartnerDetailPage } from './pages/tpo/PartnerDetail';
// Page 36/N — the TpoReportsPage was implemented but never mounted in the router.
// A TPO with a 'Reports' nav link would 404. Route now wired.
import { TpoReportsPage } from './pages/tpo/Reports';
import { TpoIntegrityFlagsPage } from './pages/tpo/IntegrityFlags';
import { TpoHolidaysPage } from './pages/tpo/Holidays';
import { TpoRoomsPage } from './pages/tpo/Rooms';
import { TpoGrievancesPage } from './pages/tpo/Grievances';
import { TpoRectificationPage } from './pages/tpo/Rectification';

// Group
import { GroupOverviewPage } from './pages/group/Overview';
import { GroupCollegesPage } from './pages/group/Colleges';
import { GroupAffiliationsPage } from './pages/group/Affiliations';
import { GroupApprovalsPage } from './pages/group/Approvals';
import { GroupApprovalDetailPage } from './pages/group/ApprovalDetail';
import { GroupGoverningEntitiesPage } from './pages/group/GoverningEntities';
import { GroupGoverningEntityDetailPage } from './pages/group/GoverningEntityDetail';
import { GroupProgramsPage } from './pages/group/Programs';
import { GroupSpecializationsPage } from './pages/group/Specializations';
import { GroupDrivesPage } from './pages/group/Drives';
import { GroupStructurePage } from './pages/group/Structure';
import { GroupTeamPage } from './pages/group/Team';
import { GroupAnalyticsPage } from './pages/group/Analytics';
import { GroupMousPage } from './pages/group/Mous';
import { GroupReportsPage } from './pages/group/Reports';
import { GroupBillingPage } from './pages/group/Billing';
import { GroupCompliancePage } from './pages/group/Compliance';
import { GroupCollegeDetailPage } from './pages/group/CollegeDetail';
import { GroupCollegeNewPage } from './pages/group/CollegeNew';
import { GroupStudentsPage } from './pages/group/Students';
import { GroupPartnersPage } from './pages/group/Partners';
import { GroupPartnerDetailPage } from './pages/group/PartnerDetail';
import { GroupMouDetailPage } from './pages/group/MouDetail';
import { GroupSettingsPage } from './pages/group/Settings';
import { GroupStudentDetailPage } from './pages/group/StudentDetail';

// Company
import { CompanyCampusHiringPage } from './pages/company/CampusHiring';
import { CompanyCandidatesPage } from './pages/company/Candidates';
import { CompanyOffersPage } from './pages/company/Offers';
import { CompanyPanelInterviewsPage } from './pages/company/PanelInterviews';
import { CompanyOpeningsPage } from './pages/company/Openings';
import { CompanyOpeningFormPage } from './pages/company/OpeningForm';
import { CompanyCollegesPage } from './pages/company/Colleges';
import { CompanyEarningsPage } from './pages/company/Earnings';
import { CompanyTeamPage } from './pages/company/Team';
import { CompanyComingSoonPage } from './pages/company/ComingSoon';
import { WorkflowsPage } from './pages/shared/Workflows';
import { WorkflowDetailPage } from './pages/shared/WorkflowDetail';
import { WorkflowNewPage } from './pages/shared/WorkflowNew';

// Principal / HoD / Faculty
import { PrincipalOverviewPage } from './pages/principal/Overview';
import { HodDepartmentPage } from './pages/hod/Department';
import { FacultyMenteesPage } from './pages/faculty/Mentees';
import { FacultyProfilePage } from './pages/faculty/Profile';

// Platform (superadmin)
import { PlatformDashboardPage } from './pages/platform/Dashboard';
import { PlatformApprovalsPage } from './pages/platform/Approvals';
import { PlatformApprovalDetailPage } from './pages/platform/ApprovalDetail';
import { PlatformTenantsPage } from './pages/platform/Tenants';
import { PlatformAuditPage } from './pages/platform/AuditLog';
import { PlatformProvisionPage } from './pages/platform/Provision';
import { PlatformFraudQueuePage } from './pages/platform/FraudQueue';
import { PlatformPlansPage } from './pages/platform/Plans';
import { PlatformPlanDetailPage } from './pages/platform/PlanDetail';
import { PlatformInvoicesPage } from './pages/platform/Invoices';
import { PlatformCatalogTransactionsPage } from './pages/platform/CatalogTransactions';
import { PlatformIncidentsPage } from './pages/platform/Incidents';
import { PlatformAnalyticsAgreementsPage } from './pages/platform/AnalyticsAgreements';
import { PlatformAnalyticsAgreementDetailPage } from './pages/platform/AnalyticsAgreementDetail';
import { PlatformHealthPage } from './pages/platform/Health';
import { PlatformFlagsPage } from './pages/platform/Flags';
import { PlatformUsersPage } from './pages/platform/Users';
import { PlatformUserDetailPage } from './pages/platform/UserDetail';
import { PlatformCollegesPage } from './pages/platform/Colleges';
import { PlatformCollegeDetailPage } from './pages/platform/CollegeDetail';
import { PlatformTenantDetailPage } from './pages/platform/TenantDetail';

// Workflows
import { WorkflowLibraryPage } from './pages/workflow/Library';
import { WorkflowBuilderPage } from './pages/workflow/Builder';
import { WorkflowRunPage } from './pages/workflow/Run';
import { WorkflowRunsPage } from './pages/workflow/Runs';

// Shared
import { NexiePage } from './pages/shared/Nexie';
import NexieDrafts from './pages/shared/NexieDrafts';
import NexieAudit from './pages/shared/NexieAudit';
import { SkillsCatalogPage } from './pages/shared/SkillsCatalog';
import { SkillDetailPage } from './pages/shared/SkillDetail';
import { NotificationsPage } from './pages/shared/Notifications';
import { PrivacyPage } from './pages/shared/Privacy';
import { GrievancesPage } from './pages/shared/Grievances';
import { RectificationPage } from './pages/shared/Rectification';
import { ConsentsPage } from './pages/shared/Consents';
import { StudentAssignmentsPage } from './pages/student/Assignments';
import { StudentTransactionsPage } from './pages/student/Transactions';
import { HodFacultyPage } from './pages/hod/Faculty';
import { HelpPage } from './pages/shared/Help';
import { SettingsPage } from './pages/shared/Settings';
import { StudentMockReportPage } from './pages/student/MockReport';
import { StudentProgressPage } from './pages/student/Progress';
import { AssessBankPage } from './pages/assess/Bank';
import { AssessExamsPage } from './pages/assess/Exams';
import { AssessTakePage } from './pages/assess/Take';
import { AssessResultsPage } from './pages/assess/Results';
import { AssessIndexPage } from './pages/assess/Index';
import { AssessQuestionEditorPage } from './pages/assess/QuestionEditor';
import { AssessAttemptPage } from './pages/assess/Attempt';
import { AssessBuilderPage } from './pages/assess/Builder';
import { AssessExamDetailPage } from './pages/assess/ExamDetail';

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  // Marketing pages — full public chrome (header + footer)
  {
    element: <PublicShell />,
    errorElement: <NotFoundPage />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/for-companies', element: <ForCompaniesPage /> },
      { path: '/for-students', element: <ForStudentsPage /> },
      { path: '/for-institutions', element: <ForInstitutionsPage /> },
      { path: '/for-partners', element: <ForPartnersPage /> },
      { path: '/about', element: <AboutPage /> },
      { path: '/terms', element: <TermsPage /> },
      { path: '/privacy', element: <PrivacyPublicPage /> },
      { path: '/status', element: <StatusPage /> },
      { path: '/customers', element: <CustomersPage /> },
      { path: '/changelog', element: <ChangelogPage /> },
      { path: '/ai', element: <AiPage /> },
      { path: '/assessments', element: <AssessmentsPage /> },
      { path: '/security', element: <SecurityPage /> },
      { path: '/contact', element: <ContactPage /> },
      { path: '/error', element: <ErrorPage /> },
      { path: '/checkout', element: <CheckoutPage /> },
    ],
  },
  // Auth pages — slim chrome
  {
    element: <AuthShell alt="signup" />,
    errorElement: <NotFoundPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  {
    element: <AuthShell alt="signin" />,
    errorElement: <NotFoundPage />,
    children: [
      { path: '/register', element: <RegisterPage /> },
      { path: '/register/student', element: <RegisterStudentPage /> },
      { path: '/register/institution', element: <RegisterInstitutionPage /> },
      { path: '/register/partner', element: <RegisterPartnerPage /> },
      // Legacy path — /register/company was routing to the institution wizard.
      // Kept as an alias to /register/partner so any outbound links continue to work.
      { path: '/register/company', element: <RegisterPartnerPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/verify-email/:token', element: <VerifyEmailPage /> },
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      { path: '/signin-link', element: <SignInLinkPage /> },
    ],
  },

  // App-shell routes — require auth
  {
    element: <AppShell />,
    children: [
      // Student
      { path: '/home', element: <RoleHomeGate /> },
      { path: '/explore', element: <ExplorePage /> },
      { path: '/open-to-me', element: <StudentEligibleDrivesPage /> },
      { path: '/opportunities/:id', element: <StudentOpportunityDetailPage /> },
      { path: '/group/opportunities', element: <TenantOpportunitiesPage /> },
      { path: '/tpo/opportunities', element: <TenantOpportunitiesPage /> },
      { path: '/applications', element: <StudentApplicationsPage /> },
      { path: '/applications/:id', element: <StudentApplicationDetailPage /> },
      { path: '/offers', element: <StudentOffersPage /> },
      { path: '/offers/:id', element: <OfferDetailPage /> },
      { path: '/interviews', element: <StudentInterviewsPage /> },
      { path: '/interviews/:id', element: <InterviewDetailPage /> },
      { path: '/profile', element: <StudentProfilePage /> },
      { path: '/profile/badges/:id', element: <BadgeDetailPage /> },
      { path: '/profile/memberships', element: <StudentMembershipsPage /> },
      { path: '/profile-360', element: <StudentProfile360Page /> },
      { path: '/profile/integrity', element: <StudentIntegrityPage /> },
      { path: '/activity', element: <StudentActivityPage /> },
      { path: '/resume-builder', element: <StudentResumeBuilderPage /> },
      { path: '/mock', element: <StudentMockPage /> },
      { path: '/mock/:id', element: <StudentMockSessionPage /> },
      { path: '/advisor', element: <StudentAdvisorPage /> },
      { path: '/upload-marks', element: <StudentUploadMarksPage /> },
      { path: '/prep', element: <StudentPrepPage /> },
      { path: '/academics', element: <StudentAcademicsPage /> },
      { path: '/documents', element: <StudentDocumentsPage /> },
      { path: '/skills', element: <StudentSkillsPage /> },
      { path: '/projects', element: <StudentProjectsPage /> },
      { path: '/stats', element: <StudentStatsPage /> },
      { path: '/tests', element: <StudentTestsPage /> },
      { path: '/saved', element: <StudentSavedPage /> },
      { path: '/internships', element: <StudentInternshipsPage /> },
      { path: '/programs', element: <StudentProgramsPage /> },

      // TPO
      { path: '/tpo/today', element: <TpoTodayPage /> },
      { path: '/tpo/drives', element: <TpoDrivesPage /> },
      { path: '/tpo/drives/new', element: <PostDrivePage /> },
      { path: '/tpo/drives/:id', element: <DriveDetailPage /> },
      { path: '/tpo/drives/:driveId/applications/:appId', element: <TpoApplicationDetailPage /> },
      { path: '/tpo/calendar', element: <TpoCalendarPage /> },
      { path: '/tpo/offers', element: <TpoOffersPage /> },
      { path: '/tpo/students', element: <TpoStudentsPage /> },
      { path: '/tpo/students/claim', element: <TpoClaimQueuePage /> },
      { path: '/tpo/memberships/auto-approved', element: <TpoAutoApprovedMembershipsPage /> },
      { path: '/tpo/settings/domains', element: <TpoDomainsPage /> },
      { path: '/tpo/campus-invitations', element: <TpoCampusInvitationsPage /> },
      { path: '/tpo/graduation', element: <TpoGraduationBatchPage /> },
      { path: '/tpo/partners', element: <TpoPartnersPage /> },
      { path: '/tpo/partners/:id', element: <TpoPartnerDetailPage /> },
      { path: '/tpo/reports', element: <TpoReportsPage /> },
      { path: '/tpo/integrity', element: <TpoIntegrityFlagsPage /> },
      { path: '/tpo/holidays', element: <TpoHolidaysPage /> },
      { path: '/tpo/rooms', element: <TpoRoomsPage /> },
      { path: '/tpo/grievances', element: <TpoGrievancesPage /> },
      { path: '/tpo/rectification', element: <TpoRectificationPage /> },
      // Aliases — same tenant-scoped pages, mounted for every reviewer shell so
      // group_admin / principal / vice_principal / hod / registrar can reach
      // them from their own sidebar. Server-side REVIEWER_ROLES gates the API.
      { path: '/group/grievances', element: <TpoGrievancesPage /> },
      { path: '/group/rectification', element: <TpoRectificationPage /> },
      { path: '/principal/grievances', element: <TpoGrievancesPage /> },
      { path: '/principal/rectification', element: <TpoRectificationPage /> },
      { path: '/hod/grievances', element: <TpoGrievancesPage /> },
      { path: '/hod/rectification', element: <TpoRectificationPage /> },
      // Backwards-compat: old /tpo/companies bookmarks and email links.
      { path: '/tpo/companies', element: <Navigate to="/tpo/partners" replace /> },
      { path: '/tpo/companies/:id', element: <TpoCompaniesRedirect /> },

      // Group
      { path: '/group/overview', element: <GroupOverviewPage /> },
      { path: '/group/colleges', element: <GroupCollegesPage /> },
      { path: '/group/affiliations', element: <GroupAffiliationsPage /> },
      { path: '/group/approvals', element: <GroupApprovalsPage /> },
      { path: '/group/approvals/:id', element: <GroupApprovalDetailPage /> },
      { path: '/group/governing-entities', element: <GroupGoverningEntitiesPage /> },
      { path: '/group/governing-entities/:id', element: <GroupGoverningEntityDetailPage /> },
      { path: '/group/programs', element: <GroupProgramsPage /> },
      { path: '/group/specializations', element: <GroupSpecializationsPage /> },
      { path: '/group/drives', element: <GroupDrivesPage /> },
      { path: '/group/structure', element: <GroupStructurePage /> },
      { path: '/group/team', element: <GroupTeamPage /> },
      { path: '/group/analytics', element: <GroupAnalyticsPage /> },
      { path: '/group/mous', element: <GroupMousPage /> },
      { path: '/group/reports', element: <GroupReportsPage /> },
      { path: '/group/billing', element: <GroupBillingPage /> },
      { path: '/group/compliance', element: <GroupCompliancePage /> },
      { path: '/group/colleges/new', element: <GroupCollegeNewPage /> },
      { path: '/group/colleges/:id', element: <GroupCollegeDetailPage /> },
      { path: '/group/partners', element: <GroupPartnersPage /> },
      { path: '/group/partners/:id', element: <GroupPartnerDetailPage /> },
      { path: '/group/mous/:id', element: <GroupMouDetailPage /> },
      { path: '/group/settings', element: <GroupSettingsPage /> },
      { path: '/group/students', element: <GroupStudentsPage /> },
      { path: '/group/students/:id', element: <GroupStudentDetailPage /> },

      // Company
      { path: '/company/campus-hiring', element: <CompanyCampusHiringPage /> },
      { path: '/company/candidates', element: <CompanyCandidatesPage /> },
      { path: '/company/offers', element: <CompanyOffersPage /> },
      { path: '/company/panel/interviews', element: <CompanyPanelInterviewsPage /> },
      { path: '/company/openings', element: <CompanyOpeningsPage /> },
      { path: '/company/openings/new', element: <CompanyOpeningFormPage /> },
      { path: '/company/openings/:id', element: <CompanyOpeningFormPage /> },
      { path: '/company/colleges', element: <CompanyCollegesPage /> },
      { path: '/company/team', element: <CompanyTeamPage /> },
      { path: '/company/users', element: <CompanyTeamPage /> },
      { path: '/company/coming-soon', element: <CompanyComingSoonPage /> },
      { path: '/company/earnings', element: <CompanyEarningsPage /> },
      // Partner-shell entry to the assessments UI (which lives at /assess/*).
      { path: '/company/assessments', element: <Navigate to="/assess/exams" replace /> },
      { path: '/workflows', element: <WorkflowsPage /> },
      { path: '/workflows/new', element: <WorkflowNewPage /> },
      { path: '/workflows/:id', element: <WorkflowDetailPage /> },

      // Principal / HoD / Faculty
      { path: '/principal/overview', element: <PrincipalOverviewPage /> },
      { path: '/hod/department', element: <HodDepartmentPage /> },
      { path: '/faculty/mentees', element: <FacultyMenteesPage /> },
      { path: '/faculty/profile', element: <FacultyProfilePage /> },

      // Platform ops
      { path: '/platform', element: <PlatformDashboardPage /> },
      { path: '/platform/approvals', element: <PlatformApprovalsPage /> },
      { path: '/platform/approvals/:id', element: <PlatformApprovalDetailPage /> },
      { path: '/platform/tenants', element: <PlatformTenantsPage /> },
      { path: '/platform/tenants/:id', element: <PlatformTenantDetailPage /> },
      { path: '/platform/audit', element: <PlatformAuditPage /> },
      { path: '/platform/plans', element: <PlatformPlansPage /> },
      { path: '/platform/plans/:id', element: <PlatformPlanDetailPage mode="view" /> },
      { path: '/platform/plans/:id/edit', element: <PlatformPlanDetailPage mode="edit" /> },
      { path: '/platform/invoices', element: <PlatformInvoicesPage /> },
      { path: '/platform/catalog-transactions', element: <PlatformCatalogTransactionsPage /> },
      { path: '/platform/incidents', element: <PlatformIncidentsPage /> },
      { path: '/platform/analytics-agreements', element: <PlatformAnalyticsAgreementsPage /> },
      { path: '/platform/analytics-agreements/:id', element: <PlatformAnalyticsAgreementDetailPage /> },
      { path: '/platform/health', element: <PlatformHealthPage /> },
      { path: '/platform/flags', element: <PlatformFlagsPage /> },
      { path: '/platform/users', element: <PlatformUsersPage /> },
      { path: '/platform/users/:id', element: <PlatformUserDetailPage /> },
      { path: '/platform/colleges', element: <PlatformCollegesPage /> },
      { path: '/platform/colleges/:id', element: <PlatformCollegeDetailPage /> },
      { path: '/platform/provision', element: <PlatformProvisionPage /> },
      { path: '/platform/fraud', element: <PlatformFraudQueuePage /> },

      // Workflows
      { path: '/workflows', element: <WorkflowLibraryPage /> },
      { path: '/workflows/builder', element: <WorkflowBuilderPage /> },
      { path: '/workflows/run', element: <WorkflowRunPage /> },
      { path: '/workflows/runs', element: <WorkflowRunsPage /> },

      // Shared
      { path: '/nexie', element: <NexiePage /> },
      { path: '/nexie/drafts', element: <NexieDrafts /> },
      { path: '/nexie/audit', element: <NexieAudit /> },
      { path: '/skills-catalog', element: <SkillsCatalogPage /> },
      { path: '/skills-catalog/:slug', element: <SkillDetailPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/privacy', element: <PrivacyPage /> },
      { path: '/settings/privacy/grievances', element: <GrievancesPage /> },
      { path: '/settings/privacy/rectification', element: <RectificationPage /> },
      { path: '/settings/privacy/consents', element: <ConsentsPage /> },
      { path: '/assignments', element: <StudentAssignmentsPage /> },
      { path: '/transactions', element: <StudentTransactionsPage /> },
      { path: '/hod/faculty', element: <HodFacultyPage /> },
      { path: '/faculty/directory', element: <HodFacultyPage /> },
      { path: '/help', element: <HelpPage /> },
      { path: '/mock-report', element: <StudentMockReportPage /> },
      { path: '/progress', element: <StudentProgressPage /> },
      { path: '/assess', element: <AssessIndexPage /> },
      { path: '/assess/bank', element: <AssessBankPage /> },
      { path: '/assess/bank/questions/new', element: <AssessQuestionEditorPage /> },
      { path: '/assess/bank/questions/:id', element: <AssessQuestionEditorPage /> },
      { path: '/assess/exams', element: <AssessExamsPage /> },
      { path: '/assess/exams/new', element: <AssessBuilderPage /> },
      { path: '/assess/exams/:id', element: <AssessExamDetailPage /> },
      { path: '/assess/exams/:id/edit', element: <AssessBuilderPage /> },
      { path: '/assess/take/:examId', element: <AssessTakePage /> },
      { path: '/assess/exams/:examId/results', element: <AssessResultsPage /> },
      { path: '/assess/attempts/:attemptId', element: <AssessAttemptPage /> },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
